import { useState } from "react";
import { useLocation } from "wouter";
import { ClipboardList, Filter, Pencil, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  useDeleteOrderV2,
  useOrderGroupsV2,
  useUpdateOrderStatus,
  useUpdateOrderV2,
  type ShipmentShortageEntry,
} from "@/hooks/useOrdersV2";
import OrderGroupTable from "@/features/orders/OrderGroupTable";
import ShippingTransitionDialog from "@/features/orders/ShippingTransitionDialog";
import { OrderGroupV2, OrderStatusV2, OrderV2, UpdateOrderStatusRequest } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TRANSITION_LABELS: Partial<Record<OrderStatusV2, string>> = {
  preparing: "開始備貨",
  ready_to_ship: "轉為待出貨",
  shipping: "安排出貨",
  delivered: "完成簽收",
  archived: "封存",
};

const COMPLETED_STATUSES: OrderStatusV2[] = ["delivered", "archived"];
const STATUS_FILTER_OPTIONS: Array<{ value: OrderStatusV2; label: string }> = [
  { value: "pending", label: "待處理" },
  { value: "preparing", label: "備貨中" },
  { value: "ready_to_ship", label: "待出貨" },
  { value: "shipping", label: "配送中" },
  { value: "delivered", label: "已完成" },
  { value: "archived", label: "已封存" },
];

interface EditItem {
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice?: number | null;
  unit?: string;
}

export interface OrderQueueWorkstreamProps {
  showConfirmDialog: (message: string, onConfirm: () => void) => void;
  showSummaryCards?: boolean;
  visibleStatuses?: OrderStatusV2[];
}

function isCompleted(status: OrderStatusV2) {
  return COMPLETED_STATUSES.includes(status);
}

function buildGroupPrintUrl(groupId: string) {
  return `/print/group/${encodeURIComponent(groupId)}?autoPrint=1`;
}

function formatQueueDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString("zh-TW", {
    month: "long",
    day: "numeric",
    weekday: "short",
  });
}

function getOrderQuantity(order: OrderV2) {
  return order.items.reduce((sum, item) => sum + item.quantity, 0);
}

function formatMaterialQuantity(quantity: number) {
  return new Intl.NumberFormat("zh-TW", { maximumFractionDigits: 6 }).format(quantity);
}

export default function OrderQueueWorkstream({
  showConfirmDialog,
  showSummaryCards = true,
  visibleStatuses,
}: OrderQueueWorkstreamProps) {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [statusFilter, setStatusFilter] = useState<OrderStatusV2 | "">("");

  const {
    data: rawGroups = [],
    isLoading,
    isFetching,
    refetch,
  } = useOrderGroupsV2(statusFilter ? { status: statusFilter } : {});

  const updateStatus = useUpdateOrderStatus();
  const updateOrderV2 = useUpdateOrderV2();
  const deleteOrderV2 = useDeleteOrderV2();

  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingOrderId, setShippingOrderId] = useState<string | null>(null);
  const [shippingOrderGroupId, setShippingOrderGroupId] = useState<string | null>(null);
  const [shippingMethodChoice, setShippingMethodChoice] = useState<"self_delivery" | "logistics">("self_delivery");
  const [carrierName, setCarrierName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipmentShortageNotice, setShipmentShortageNotice] = useState<{
    orderId: string;
    items: ShipmentShortageEntry[];
    printUrl: string | null;
  } | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OrderV2 | null>(null);
  const [editOrderDate, setEditOrderDate] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editItems, setEditItems] = useState<EditItem[]>([]);

  const canTransition = hasRole("admin", "staff");
  const canDelete = hasRole("admin");
  const canEdit = hasRole("admin");
  const canPrint = hasRole("admin", "staff");
  // deep-review 126：/sign 簽收頁僅 admin/driver 可進（App.tsx RequireAuth）。
  // staff 不具此權限，故自送單「前往簽收」動作對 staff 停用，避免點擊被靜默重導。
  const canSign = hasRole("admin", "driver");

  const allowedStatusSet = visibleStatuses ? new Set(visibleStatuses) : null;
  const availableStatusOptions = allowedStatusSet
    ? STATUS_FILTER_OPTIONS.filter((option) => allowedStatusSet.has(option.value))
    : STATUS_FILTER_OPTIONS;
  const groups = rawGroups
    .map((group) => {
      if (!allowedStatusSet) {
        return group;
      }

      const visibleOrders = group.orders.filter((order) => allowedStatusSet.has(order.status));
      if (visibleOrders.length === 0) {
        return null;
      }

      return {
        ...group,
        orders: visibleOrders,
        orderCount: visibleOrders.length,
        totalQuantity: visibleOrders.reduce((sum, order) => sum + getOrderQuantity(order), 0),
      };
    })
    .filter((group): group is OrderGroupV2 => group !== null);

  const groupsByDate = groups.reduce<Record<string, OrderGroupV2[]>>((result, group) => {
    if (!result[group.orderDate]) {
      result[group.orderDate] = [];
    }

    result[group.orderDate].push(group);
    return result;
  }, {});

  const sortedDates = Object.keys(groupsByDate).sort((left, right) => right.localeCompare(left));
  const flattenedOrders = groups.flatMap((group) => group.orders);

  const resetShippingDialogState = () => {
    setShippingDialogOpen(false);
    setShippingOrderId(null);
    setShippingOrderGroupId(null);
    setShippingMethodChoice("self_delivery");
    setCarrierName("");
    setTrackingNumber("");
  };

  const handleTransition = (order: OrderV2, targetStatus: OrderStatusV2) => {
    if (targetStatus === "shipping") {
      setShippingOrderId(order.id);
      setShippingOrderGroupId(order.groupId);
      setShippingMethodChoice(order.shippingMethod === "logistics" ? "logistics" : "self_delivery");
      setCarrierName("");
      setTrackingNumber("");
      setShippingDialogOpen(true);
      return;
    }

    if (targetStatus === "delivered" && order.shippingMethod === "self_delivery") {
      setLocation(`/sign/${order.id}`);
      return;
    }

    const data: UpdateOrderStatusRequest = { status: targetStatus };

    showConfirmDialog(`要將這筆訂單狀態更新為「${TRANSITION_LABELS[targetStatus] || targetStatus}」嗎？`, async () => {
      try {
        await updateStatus.mutateAsync({ id: order.id, data });
        toast({ title: "狀態已更新" });
      } catch (error: any) {
        toast({
          title: "狀態更新失敗",
          description: error.message || "請稍後再試",
          variant: "destructive",
        });
      }
    });
  };

  const handleShippingConfirm = async () => {
    if (!shippingOrderId) {
      return;
    }

    const normalizedCarrierName = carrierName.trim();
    const normalizedTrackingNumber = trackingNumber.trim();

    if (shippingMethodChoice === "logistics" && !normalizedCarrierName) {
      toast({
        title: "請填寫物流公司",
        description: "選擇物流配送時，必須輸入承運公司名稱。",
        variant: "destructive",
      });
      return;
    }

    if (shippingMethodChoice === "logistics" && !normalizedTrackingNumber) {
      toast({
        title: "請填寫追蹤編號",
        description: "選擇物流配送時，必須輸入託運或追蹤編號。",
        variant: "destructive",
      });
      return;
    }

    const data: UpdateOrderStatusRequest = {
      status: "shipping",
      shippingMethod: shippingMethodChoice,
      carrierName: shippingMethodChoice === "logistics" ? normalizedCarrierName : undefined,
      trackingNumber: shippingMethodChoice === "logistics" ? normalizedTrackingNumber : undefined,
    };
    const printUrl = shippingOrderGroupId ? buildGroupPrintUrl(shippingOrderGroupId) : null;
    const pendingPrintWindow = printUrl ? window.open("", "_blank") : null;

    try {
      const updatedOrder = await updateStatus.mutateAsync({ id: shippingOrderId, data });
      const shortages = updatedOrder.shipmentShortages ?? [];
      if (shortages.length > 0) {
        // Keep the shortage warning in front; print from the warning after it is read.
        if (pendingPrintWindow && !pendingPrintWindow.closed) {
          pendingPrintWindow.close();
        }
        setShipmentShortageNotice({ orderId: shippingOrderId, items: shortages, printUrl });
      } else if (printUrl) {
        if (pendingPrintWindow && !pendingPrintWindow.closed) {
          pendingPrintWindow.location.replace(printUrl);
          pendingPrintWindow.focus?.();
        } else {
          toast({
            title: "出貨已更新",
            description: "列印視窗未成功保留，請允許彈出視窗後再試一次。",
            variant: "destructive",
          });
        }
      }

      toast({ title: "已進入出貨流程" });
      resetShippingDialogState();
    } catch (error: any) {
      if (pendingPrintWindow && !pendingPrintWindow.closed) {
        pendingPrintWindow.close();
      }

      toast({
        title: "出貨更新失敗",
        description: error.message || "請稍後再試",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (order: OrderV2) => {
    showConfirmDialog("確定要刪除這筆訂單嗎？此動作無法復原。", async () => {
      try {
        await deleteOrderV2.mutateAsync(order.id);
        toast({ title: "訂單已刪除" });
      } catch (error: any) {
        toast({
          title: "刪除失敗",
          description: error.message || "請稍後再試",
          variant: "destructive",
        });
      }
    });
  };

  const handleEdit = (order: OrderV2) => {
    setEditingOrder(order);
    setEditOrderDate(order.orderDate);
    setEditNotes(order.notes || "");
    setEditItems(order.items.map((item) => ({ ...item })));
    setEditDialogOpen(true);
  };

  const handleEditConfirm = async () => {
    if (!editingOrder) {
      return;
    }

    // G-12: 對齊 OrderForm 與後端 updateOrderV2Schema 的前端驗證，
    // 非法輸入在此即擋下，不送出請求（避免後端 400/500 才被擋）。
    // 日期格式對齊後端 dateSchema (YYYY-MM-DD)。
    if (!editOrderDate || !/^\d{4}-\d{2}-\d{2}$/.test(editOrderDate)) {
      toast({ title: "請選擇正確的出貨日期", variant: "destructive" });
      return;
    }

    if (editItems.length === 0) {
      toast({ title: "請至少保留一個品項", variant: "destructive" });
      return;
    }

    for (const item of editItems) {
      // 數量：正數、最多兩位小數（後端 quantitySchema：positive + multipleOf 0.01）
      if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
        toast({
          title: `品項「${item.productName}」數量需為正數`,
          variant: "destructive",
        });
        return;
      }

      // 以容差比較判斷「是否為整數分位（最多兩位小數）」，避免浮點誤判：
      // 例如 19.99 * 100 = 1998.9999999999998，直接用 !== 比較會把後端
      // quantitySchema（multipleOf 0.01）明確接受的合法兩位小數誤判為非法。
      const quantityCents = item.quantity * 100;
      if (Math.abs(quantityCents - Math.round(quantityCents)) > 1e-6) {
        toast({
          title: `品項「${item.productName}」數量最多兩位小數`,
          variant: "destructive",
        });
        return;
      }

      // R7b：單價必填（後端 updateOrderV2Schema 同規則）。0 是合法的成交價（樣品／換貨／折讓），
      // 沒填才算缺價——包含歷史上沒有單價的品項：儲存會整批取代品項，所以要先補上成交價。
      const price = item.unitPrice;
      if (price === null || price === undefined) {
        toast({
          title: `品項「${item.productName}」沒有單價`,
          description: "請輸入成交價；樣品／換貨／折讓請輸入 0。",
          variant: "destructive",
        });
        return;
      }

      // 邊界對齊 OrderForm.tsx：需為 0 以上、且在合理上限內。
      if (!Number.isFinite(price) || price < 0 || price >= 1_000_000) {
        toast({
          title: `品項「${item.productName}」單價不合法（需為 0 以上且小於 1,000,000）`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      await updateOrderV2.mutateAsync({
        id: editingOrder.id,
        data: {
          customerId: editingOrder.customerId!,
          orderDate: editOrderDate,
          notes: editNotes || undefined,
          items: editItems.map((item) => ({
            productCode: item.productCode,
            productName: item.productName,
            quantity: item.quantity,
            unitPrice: item.unitPrice as number,
            unit: item.unit,
          })),
        },
      });

      toast({ title: "訂單已更新" });
      setEditDialogOpen(false);
      setEditingOrder(null);
    } catch (error: any) {
      toast({
        title: "更新失敗",
        description: error.message || "請稍後再試",
        variant: "destructive",
      });
    }
  };

  const getActionLabel = (order: OrderV2, next: OrderStatusV2) => {
    if (next === "delivered" && order.shippingMethod === "self_delivery") {
      return "前往簽收";
    }

    return TRANSITION_LABELS[next] || next;
  };

  return (
    <div data-testid="ordersList" className="space-y-5">
      <ShippingTransitionDialog
        open={shippingDialogOpen}
        onOpenChange={(open) => {
          setShippingDialogOpen(open);
          if (!open) {
            resetShippingDialogState();
          }
        }}
        shippingMethodChoice={shippingMethodChoice}
        onShippingMethodChange={setShippingMethodChoice}
        carrierName={carrierName}
        onCarrierNameChange={setCarrierName}
        trackingNumber={trackingNumber}
        onTrackingNumberChange={setTrackingNumber}
        onCancel={() => setShippingDialogOpen(false)}
        onConfirm={handleShippingConfirm}
        isPending={updateStatus.isPending}
      />
      <Dialog
        open={shipmentShortageNotice !== null}
        onOpenChange={(open) => { if (!open) setShipmentShortageNotice(null); }}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto border border-block/40 bg-surface text-ink shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-block">出貨已完成，原料庫存為負</DialogTitle>
            <DialogDescription className="text-ink-muted">
              訂單 <span className="break-all">{shipmentShortageNotice?.orderId}</span> 已完成出貨扣料。請核對以下原物料庫存並儘速處理短缺；不需重複安排出貨。
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-64 space-y-2 overflow-y-auto" aria-label="原物料短缺明細">
            {shipmentShortageNotice?.items.map((item) => (
              <li key={item.rawMaterialId} className="rounded-md border border-block/30 bg-block-tint p-3 text-sm text-ink">
                <p className="font-semibold">{item.rawMaterialCode} {item.rawMaterialName}</p>
                <p className="mt-1 text-block">短缺 {formatMaterialQuantity(item.shortageQuantity)}</p>
                <p className="text-ink-muted">扣料前 {formatMaterialQuantity(item.currentBalance)}；本次扣料 {formatMaterialQuantity(item.deductedQuantity)}</p>
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-muted">數量依各原物料設定的單位計算，請至原物料管理查核。</p>
          <DialogFooter className="border-t border-line pt-4">
            {shipmentShortageNotice?.printUrl && (
              <Button type="button" variant="outline" onClick={() => window.open(shipmentShortageNotice.printUrl!, "_blank")}>列印出貨單</Button>
            )}
            <Button type="button" onClick={() => setShipmentShortageNotice(null)} className="bg-action text-white hover:bg-action-hover">我已了解</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditingOrder(null);
          }
        }}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto border border-line bg-surface text-ink sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-ink">
              <Pencil className="h-5 w-5 text-progress" />
              編輯訂單
            </DialogTitle>
            <DialogDescription className="text-sm text-ink-muted">
              目前編輯客戶：{editingOrder?.customerName || "未指定客戶"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-muted">出貨日期</label>
              <Input
                type="date"
                value={editOrderDate}
                onChange={(event) => setEditOrderDate(event.target.value)}
                className="glass-input h-10"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-muted">備註</label>
              <Input
                value={editNotes}
                onChange={(event) => setEditNotes(event.target.value)}
                placeholder="輸入訂單備註"
                className="glass-input h-10"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-ink-muted">品項明細，共 {editItems.length} 項</label>
              <div className="overflow-hidden rounded-lg border border-line-strong/60">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line-strong/60 bg-raised/60">
                      <th className="p-2 text-left text-xs font-medium text-ink-muted">產品</th>
                      <th className="w-24 p-2 text-right text-xs font-medium text-ink-muted">數量</th>
                      <th className="w-24 p-2 text-right text-xs font-medium text-ink-muted">單價</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editItems.map((item, index) => (
                      <tr key={index} className="border-b border-line-strong/30 last:border-0">
                        <td className="p-2">
                          <span className="text-xs text-progress">{item.productCode}</span>
                          <span className="ml-1 text-xs text-ink">{item.productName}</span>
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(event) => {
                              const updated = [...editItems];
                              updated[index] = {
                                ...updated[index],
                                quantity: parseFloat(event.target.value) || 0,
                              };
                              setEditItems(updated);
                            }}
                            className="glass-input h-8 w-full text-right text-sm"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPrice ?? ""}
                            aria-label={`${item.productCode} 單價`}
                            placeholder="必填"
                            onChange={(event) => {
                              const updated = [...editItems];
                              updated[index] = {
                                ...updated[index],
                                unitPrice: event.target.value ? parseFloat(event.target.value) : null,
                              };
                              setEditItems(updated);
                            }}
                            className="glass-input h-8 w-full text-right text-sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-line pt-4">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-line-strong bg-transparent text-ink hover:bg-raised"
            >
              取消
            </Button>
            <Button
              onClick={handleEditConfirm}
              disabled={updateOrderV2.isPending}
              className="bg-action text-white hover:bg-action-hover"
            >
              {updateOrderV2.isPending ? "儲存中..." : "儲存變更"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 可讀性合約：操作列只放標題、篩選與重新整理，不放解說文案與統計卡。 */}
      <section className="rounded-md border border-line bg-surface/70 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold tracking-tight text-ink-strong">
            {showSummaryCards ? "工作清單" : "出貨前清單"}
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex h-11 items-center gap-2 rounded-md border border-line bg-canvas px-3 text-sm text-ink-muted">
              <Filter className="h-4 w-4" />
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as OrderStatusV2 | "")}
                className="bg-transparent text-sm text-ink outline-none"
              >
                <option value="">全部狀態</option>
                {/* J-04b：首頁「出貨前清單」只給出貨前的狀態，不列永遠是空的選項 */}
                {availableStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="h-11 rounded-md border-line-strong bg-canvas px-4 text-ink hover:bg-raised hover:text-ink"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              重新整理
            </Button>
          </div>
        </div>
      </section>
      <div className="min-h-[400px]">
        {isLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-line/80 bg-surface/60 py-20">
            <div className="flex animate-pulse flex-col items-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-line border-t-action"></div>
              <span className="font-medium text-ink-muted">正在讀取訂單資料...</span>
            </div>
          </div>
        ) : sortedDates.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-line-strong/60 bg-surface/50 px-4 py-20 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-raised">
              <ClipboardList className="h-8 w-8 text-ink-faint" />
            </div>
            <h3 className="text-lg font-medium text-ink">目前沒有可顯示的訂單</h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-ink-muted">
              {statusFilter ? "這個狀態目前沒有資料，可以切回全部狀態或重新整理。" : "建立新訂單後，這裡就會自動整理成日期與客戶群組。"}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {sortedDates.map((date) => {
              const dateGroups = [...(groupsByDate[date] || [])].sort((left, right) =>
                left.customerName.localeCompare(right.customerName, "zh-Hant"),
              );
              const dateOrders = dateGroups.flatMap((group) => group.orders);
              const dateActiveCount = dateOrders.filter((order) => !isCompleted(order.status)).length;
              const dateCompletedCount = dateOrders.filter((order) => isCompleted(order.status)).length;
              const dateKilograms = dateOrders.reduce((sum, order) => sum + getOrderQuantity(order), 0);

              return (
                <section
                  key={date}
                  className="overflow-hidden rounded-lg border border-line/80 bg-surface/65 shadow-sm"
                >
                  <div className="border-b border-line/80 bg-canvas/45 px-5 py-5 lg:px-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-1 rounded-full bg-progress"></div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.16em] text-ink-muted">出貨日期</p>
                            <h3 className="text-base font-bold tracking-tight text-ink-strong">{formatQueueDate(date)}</h3>
                          </div>
                        </div>
                        <p className="text-sm text-ink-muted">
                          共有 {dateGroups.length} 個客戶群組，總重量{" "}
                          <span className="tabular-nums">{dateKilograms.toFixed(1)}</span> kg。
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {dateActiveCount > 0 && (
                          <span className="rounded-full border border-warn/25 bg-warn-tint px-3 py-1 text-sm text-warn">
                            待處理 {dateActiveCount}
                          </span>
                        )}
                        {dateCompletedCount > 0 && (
                          <span className="rounded-full border border-done/25 bg-done-tint px-3 py-1 text-sm text-done">
                            已完成 {dateCompletedCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 p-4 lg:p-5">
                    {dateGroups.map((group) => (
                      <OrderGroupTable
                        key={group.groupId}
                        group={group}
                        canPrint={canPrint}
                        canTransition={canTransition}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canSign={canSign}
                        onPrintGroup={(groupId) => {
                          window.open(buildGroupPrintUrl(groupId), "_blank");
                        }}
                        onTransition={handleTransition}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        getActionLabel={getActionLabel}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
